import { useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactElement } from "react";
import { dialoguePlace, type PlaceBox } from "./place.ts";
import { say } from "../../words.ts";
import { NAME_PART_FIELD_PATTERN } from "../login-screen.tsx";
import { useIcons } from "../icons/context.tsx";
import { choiceMarkViews, markView } from "./marks.ts";
import { DialoguePanel, type DialoguePortrait } from "../../ui/kit/index.ts";
import type { InputRouter } from "../../ui/input/router.ts";
import type { LayerKeyAnswer } from "../../ui/input/router-types.ts";
import type { DialogueControl, ShownDialogue } from "./store.ts";

/**
 * The game page's dialogue window (ADR-136 decision 5.6): the kit's `DialoguePanel` over the dialogue store, sharing
 * the keyboard with the world behind it through the input router.
 *
 * - While it is open it is a layer of the router, not a hold, so the game keeps the keyboard and the movement keys
 *   still move. Escape leaves the conversation, as it closes any topmost layer. The digit keys 1 to 6 choose the choice
 *   of that number while it is topmost and no text field has focus; a digit past the choices shown is declined, so it
 *   reaches the bar. Leave is the last numbered row (the design's), so its digit, one past the choices, leaves; with
 *   no choices shown, Enter leaves too.
 * - It takes no focus as it opens, so the keys stay the game's; a click on a choice sends it and gives focus back to
 *   the page.
 * - Each node replaces the last in the same window, keeping its layer.
 * - While the server asks for the companion's name (ADR-142 decision 5.4), a name field stands in place of the choices,
 *   focused so the player types at once; Leave stays, and leaving ends the offer for now.
 * - It stands where it covers no HUD (`place.ts`): over the action bar, or above the chat panel when that would cover
 *   it, measured from the page's own HUD slots as it opens, as each node shows and whenever the page or the chat
 *   panel changes size or place.
 */

/**
 * CSS pixels of a logical pixel on this page, from the kit's `--px`.
 *
 * @returns The ratio, 1 when the property is not set.
 */
function logicalPixel(): number {
  const value = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--px"));
  return Number.isFinite(value) && value > 0 ? value : 1;
}

/**
 * The box of the first shown element a selector matches, or the union of every one for the bar's slots.
 *
 * @param selector The selector.
 * @returns The box, or null when nothing shown matches.
 */
function shownBox(selector: string): PlaceBox | null {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const element of document.querySelectorAll(selector)) {
    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      left = Math.min(left, rect.left);
      top = Math.min(top, rect.top);
      right = Math.max(right, rect.right);
      bottom = Math.max(bottom, rect.bottom);
    }
  }
  return left === Infinity ? null : { left, top, width: right - left, height: bottom - top };
}

/**
 * How the window names and draws a speaker, by its index in the NPC look table.
 */
export interface Speakers {
  readonly name: (speaker: number) => string;
  readonly portrait: (speaker: number) => DialoguePortrait | null;
}

/**
 * The digit keys a row answers to, by its index: six choices and Leave after them.
 */
const CHOICE_KEYS = ["1", "2", "3", "4", "5", "6", "7"] as const;

/**
 * The window's words for a speaker: the name a screen reader gives the dialog and the Leave button's label.
 *
 * @param speaker The speaker's name.
 * @returns The words.
 */
function dialogueWords(speaker: string): { readonly label: string; readonly leave: string } {
  return { label: say("talk.label", { name: speaker }), leave: say("talk.leave") };
}

/**
 * The window, shown while the store holds a node.
 *
 * @param props The `store`, the `input` router it opens its layer on, and `speakers`, which names and draws a speaker
 *   by its index in the NPC look table.
 * @returns The window, or nothing while closed.
 */
export function DialogueView({ store, input, speakers }: { readonly store: DialogueControl; readonly input: Pick<InputRouter, "open">; readonly speakers: Speakers }): ReactElement | null {
  const shown = useSyncExternalStore(store.subscribe, store.getSnapshot);
  return shown === null ? null : <OpenDialogue shown={shown} store={store} input={input} speaker={speakers.name(shown.line.speaker)} portrait={speakers.portrait(shown.line.speaker)} />;
}

/**
 * The window while open: its layer and its node.
 *
 * @param props The node `shown`, the `store`, the `input` router, the `speaker`'s name and its `portrait`.
 * @returns The window.
 */
function OpenDialogue({ shown, store, input, speaker, portrait }: { readonly shown: ShownDialogue; readonly store: DialogueControl; readonly input: Pick<InputRouter, "open">; readonly speaker: string; readonly portrait: DialoguePortrait | null }): ReactElement {
  const panel = useRef<HTMLDivElement>(null);
  const icons = useIcons();
  const [at, setAt] = useState<{ readonly left: number; readonly top: number } | null>(null);

  useLayoutEffect(() => {
    const place = (): void => {
      const box = panel.current;
      const area = box?.offsetParent;
      if (box === null || !(area instanceof HTMLElement)) {
        return;
      }
      const origin = area.getBoundingClientRect();
      const local = (rect: PlaceBox | null): PlaceBox | null => (rect === null ? null : { ...rect, left: rect.left - origin.left, top: rect.top - origin.top });
      const next = dialoguePlace({ width: origin.width, height: origin.height }, { width: box.offsetWidth, height: box.offsetHeight }, local(shownBox("[data-hud-slot='abilities']")), local(shownBox("[data-hud-slot='chat']")), Math.round(6 * logicalPixel()));
      setAt((last) => (last !== null && last.left === next.left && last.top === next.top ? last : next));
    };
    place();
    const watcher = new ResizeObserver(place);
    for (const element of [panel.current, document.querySelector("[data-hud-slot='chat']"), document.querySelector("[data-hud-slot='abilities']")]) {
      if (element !== null) {
        watcher.observe(element);
      }
    }
    window.addEventListener("resize", place);
    const moved = new MutationObserver(place);
    const chat = document.querySelector("[data-hud-slot='chat']");
    if (chat !== null) {
      moved.observe(chat, { attributes: true, attributeFilter: ["style"] });
    }
    return () => {
      watcher.disconnect();
      moved.disconnect();
      window.removeEventListener("resize", place);
    };
  }, [shown]);

  useLayoutEffect(() => {
    const keys: Record<string, LayerKeyAnswer> = Object.fromEntries(
      CHOICE_KEYS.map((key, index) => [
        key,
        () => {
          const current = store.getSnapshot();
          if (current !== null && index === current.line.choices.length) {
            store.leave();
            return true;
          }
          return store.choose(index);
        },
      ]),
    );
    keys["Enter"] = () => {
      const current = store.getSnapshot();
      if (current === null || current.line.choices.length > 0) {
        return false;
      }
      store.leave();
      return true;
    };
    const layer = input.open({ onClose: () => store.leave(), keys });
    return () => {
      const box = panel.current;
      if (box !== null && box.contains(document.activeElement)) {
        (document.activeElement as HTMLElement).blur();
      }
      layer.close();
    };
  }, [store, input]);

  /**
   * Sends a clicked choice and gives the keys back to the game.
   *
   * @param index The choice's index.
   */
  const choose = (index: number): void => {
    store.choose(index);
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && panel.current?.contains(focused) === true) {
      focused.blur();
    }
  };

  const leaveMark = markView(icons, "leave");
  const tier = shown.line.tier === null ? null : { name: shown.line.tier.name, at: shown.line.tier.at, of: shown.line.tier.of, label: say("talk.tier.label", { tier: shown.line.tier.name, at: String(shown.line.tier.at + 1), of: String(shown.line.tier.of) }), next: shown.line.tier.next === null ? null : say("talk.tier.next", { tier: shown.line.tier.next }), meal: shown.line.tier.hungry === null ? null : say(shown.line.tier.hungry ? "talk.tier.hungry" : "talk.tier.fed") };
  const naming = shown.naming === null ? null : { words: { label: say("talk.naming.label"), hint: say("talk.naming.hint"), submit: say("talk.naming.submit") }, pattern: NAME_PART_FIELD_PATTERN, refused: shown.naming.refused === null ? null : say(`talk.naming.refused.${shown.naming.refused}`), pending: shown.naming.pending, onSubmit: (name: string) => store.submitName(name) };
  return <DialoguePanel key={shown.serial} speaker={speaker} text={shown.line.text} choices={shown.line.choices} marks={shown.line.marks.map((choice) => choiceMarkViews(icons, choice))} words={dialogueWords(speaker)} onChoose={choose} onLeave={() => store.leave()} panel={panel} at={at} portrait={portrait} leaveMark={leaveMark} naming={naming} tier={tier} />;
}
